const INITIAL_VISIBLE_ROWS = 2;
const STORAGE_KEY = 'wifi-pcb-selections-v1';

class WifiPcBuilder extends HTMLElement {
  /** @type {Map<string, {variantId: string, price: number, title: string, image: string}>} */
  #selections = new Map();

  /** @type {Set<string>} category keys the shopper has expanded via "show all" */
  #expandedCategories = new Set();

  connectedCallback() {
    this.summary = this.querySelector('[data-wifi-pcb-summary]');
    this.summaryList = this.querySelector('[data-wifi-pcb-summary-list]');
    this.totalEl = this.querySelector('[data-wifi-pcb-total]');
    this.submitButton = this.querySelector('[data-wifi-pcb-submit]');
    this.progressFill = this.querySelector('[data-wifi-pcb-progress-fill]');
    this.progressLabel = this.querySelector('[data-wifi-pcb-progress-label]');
    this.categoryCount = this.querySelectorAll('[data-wifi-pcb-category]').length;
    this.detailsPanel = this.querySelector('[data-wifi-pcb-details]');
    this.detailsImage = this.querySelector('[data-wifi-pcb-details-image]');
    this.detailsTitle = this.querySelector('[data-wifi-pcb-details-title]');
    this.detailsPrice = this.querySelector('[data-wifi-pcb-details-price]');

    this.addEventListener('change', this.#handleChange);

    this.querySelectorAll('[data-wifi-pcb-clear]').forEach((button) => {
      button.addEventListener('click', () => this.#clearCategory(button.dataset.wifiPcbClear));
    });

    this.querySelectorAll('[data-wifi-pcb-category]').forEach((fieldset) => {
      const categoryKey = fieldset.dataset.wifiPcbCategory;
      const showMoreButton = fieldset.querySelector('[data-wifi-pcb-show-more]');
      const searchInput = fieldset.querySelector('[data-wifi-pcb-search]');

      showMoreButton?.addEventListener('click', () => {
        this.#expandedCategories.add(categoryKey);
        this.#applyCategoryVisibility(fieldset);
      });

      searchInput?.addEventListener('input', () => this.#applyCategoryVisibility(fieldset));

      this.#applyCategoryVisibility(fieldset);
    });

    this.submitButton?.addEventListener('click', this.#handleSubmit);

    this.#restoreSelections();
    this.#updateSummary();

    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.querySelectorAll('[data-wifi-pcb-category]').forEach((fieldset) => {
          this.#applyCategoryVisibility(fieldset);
        });
      }, 150);
    });

    if (this.submitButton instanceof HTMLButtonElement) {
      this.submitButtonDefaultText = this.submitButton.textContent;
    }

    // When the shopper hits the browser Back button from checkout, Chrome/Safari
    // often restore this exact page from the back-forward cache instead of
    // reloading it — so connectedCallback never runs again and the submit
    // button stays stuck disabled/"..." from the in-flight add-to-cart call.
    // Re-sync everything from scratch whenever that happens.
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      this.#resetAndResync();
    });
  }

  #resetAndResync() {
    this.#selections.clear();
    this.#expandedCategories.clear();

    this.querySelectorAll('.wifi-pcb__radio').forEach((radio) => {
      if (radio instanceof HTMLInputElement) radio.checked = false;
    });
    this.querySelectorAll('[data-wifi-pcb-clear]').forEach((button) => {
      button.hidden = true;
    });

    if (this.submitButton instanceof HTMLButtonElement) {
      this.submitButton.disabled = true;
      this.submitButton.textContent = this.submitButtonDefaultText || this.submitButton.textContent;
    }

    if (this.detailsPanel) this.detailsPanel.hidden = true;

    this.#restoreSelections();

    this.querySelectorAll('[data-wifi-pcb-category]').forEach((fieldset) => {
      this.#applyCategoryVisibility(fieldset);
    });

    this.#updateSummary();
  }

  #restoreSelections() {
    let stored;
    try {
      stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    } catch {
      return;
    }

    Object.entries(stored).forEach(([categoryKey, selection]) => {
      const fieldset = this.querySelector(`[data-wifi-pcb-category="${categoryKey}"]`);
      const radio = fieldset?.querySelector(`.wifi-pcb__radio[value="${selection.variantId}"]`);
      if (!(radio instanceof HTMLInputElement) || radio.disabled) return;

      radio.checked = true;
      this.#selections.set(categoryKey, selection);

      const clearButton = fieldset.querySelector('[data-wifi-pcb-clear]');
      if (clearButton) clearButton.hidden = false;

      // make sure a restored card that was past the initial fold is actually visible
      this.#expandedCategories.add(categoryKey);
      this.#applyCategoryVisibility(fieldset);

      this.#updateDetails(selection);
    });
  }

  #persistSelections() {
    const plain = {};
    this.#selections.forEach((selection, categoryKey) => {
      plain[categoryKey] = selection;
    });
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(plain));
    } catch {
      /* storage unavailable (private mode / quota) — selection just won't persist */
    }
  }

  /**
   * Reads the grid's actual current column count (which changes at CSS
   * breakpoints) so "2 rows" means the right number of cards at any width.
   * @param {Element} grid
   */
  #getColumnCount(grid) {
    if (!(grid instanceof HTMLElement)) return 5;
    const columns = window.getComputedStyle(grid).gridTemplateColumns.split(' ').filter(Boolean);
    return columns.length || 5;
  }

  /**
   * @param {Element} fieldset
   */
  #applyCategoryVisibility(fieldset) {
    const categoryKey = fieldset.dataset.wifiPcbCategory;
    const grid = fieldset.querySelector('[data-wifi-pcb-grid]');
    const cards = Array.from(fieldset.querySelectorAll('.wifi-pcb__card'));
    const searchInput = fieldset.querySelector('[data-wifi-pcb-search]');
    const showMoreButton = fieldset.querySelector('[data-wifi-pcb-show-more]');
    const noResults = fieldset.querySelector('[data-wifi-pcb-no-results]');
    const query = (searchInput instanceof HTMLInputElement ? searchInput.value : '').trim().toLowerCase();
    const expanded = this.#expandedCategories.has(categoryKey);
    const initialVisibleCount = this.#getColumnCount(grid) * INITIAL_VISIBLE_ROWS;

    let visibleCount = 0;
    cards.forEach((card, index) => {
      const title = card.querySelector('.wifi-pcb__radio')?.dataset.title?.toLowerCase() || '';
      const matchesSearch = query === '' || title.includes(query);
      const withinInitialLimit = query !== '' || expanded || index < initialVisibleCount;
      const visible = matchesSearch && withinInitialLimit;

      card.hidden = !visible;
      if (visible) visibleCount += 1;
    });

    if (showMoreButton instanceof HTMLElement) {
      showMoreButton.hidden = expanded || query !== '' || cards.length <= initialVisibleCount;
    }

    if (noResults instanceof HTMLElement) {
      noResults.hidden = visibleCount > 0;
    }
  }

  #handleChange = (event) => {
    const radio = event.target;
    if (!(radio instanceof HTMLInputElement) || radio.type !== 'radio') return;

    const fieldset = radio.closest('[data-wifi-pcb-category]');
    const categoryKey = fieldset?.dataset.wifiPcbCategory;
    if (!categoryKey) return;

    const selection = {
      variantId: radio.value,
      price: Number(radio.dataset.price || 0),
      title: radio.dataset.title || '',
      image: radio.dataset.image || '',
    };

    this.#selections.set(categoryKey, selection);

    const clearButton = fieldset.querySelector('[data-wifi-pcb-clear]');
    if (clearButton) clearButton.hidden = false;

    this.#updateDetails(selection);
    this.#persistSelections();
    this.#updateSummary();
  };

  /**
   * @param {{title: string, price: number, image: string}} selection
   */
  #updateDetails(selection) {
    if (!this.detailsPanel || !this.detailsImage || !this.detailsTitle || !this.detailsPrice) return;

    this.detailsPanel.hidden = false;
    if (selection.image) {
      this.detailsImage.src = selection.image;
      this.detailsImage.alt = selection.title;
      this.detailsImage.hidden = false;
    } else {
      this.detailsImage.hidden = true;
    }
    this.detailsTitle.textContent = selection.title;
    this.detailsPrice.textContent = this.#formatMoney(selection.price);
  }

  #clearCategory(categoryKey) {
    if (!categoryKey) return;
    this.#selections.delete(categoryKey);

    const fieldset = this.querySelector(`[data-wifi-pcb-category="${categoryKey}"]`);
    fieldset?.querySelectorAll('.wifi-pcb__radio').forEach((radio) => {
      if (radio instanceof HTMLInputElement) radio.checked = false;
    });

    const clearButton = fieldset?.querySelector('[data-wifi-pcb-clear]');
    if (clearButton) clearButton.hidden = true;

    this.#persistSelections();
    this.#updateSummary();
  }

  #updateSummary() {
    if (!this.summaryList || !this.totalEl || !this.submitButton) return;

    let total = 0;
    const items = [];

    this.#selections.forEach((selection, categoryKey) => {
      total += selection.price;
      items.push(
        `<li class="wifi-pcb__summary-item">` +
          `<button type="button" class="wifi-pcb__summary-remove" data-wifi-pcb-summary-remove="${categoryKey}" aria-label="إلغاء ${this.#escape(selection.title)}">×</button>` +
          `<span class="wifi-pcb__summary-item-title">${this.#escape(selection.title)}</span>` +
          `<span class="wifi-pcb__summary-item-price">${this.#formatMoney(selection.price)}</span>` +
        `</li>`
      );
    });

    this.summaryList.innerHTML = items.join('');
    this.summaryList.querySelectorAll('[data-wifi-pcb-summary-remove]').forEach((button) => {
      button.addEventListener('click', () => this.#clearCategory(button.dataset.wifiPcbSummaryRemove));
    });
    this.totalEl.textContent = this.#formatMoney(total);

    if (this.progressFill && this.progressLabel && this.categoryCount > 0) {
      const percent = Math.round((this.#selections.size / this.categoryCount) * 100);
      this.progressFill.style.width = `${percent}%`;
      this.progressLabel.textContent = `${this.#selections.size} / ${this.categoryCount}`;
    }

    if (this.#selections.size > 0) {
      this.summary?.setAttribute('data-has-items', '');
    } else {
      this.summary?.removeAttribute('data-has-items');
    }

    this.submitButton.disabled = this.#selections.size === 0;
  }

  #handleSubmit = async () => {
    if (this.#selections.size === 0 || !(this.submitButton instanceof HTMLButtonElement)) return;

    const buildId = `build-${Date.now()}`;
    const items = [];
    this.#selections.forEach((selection, categoryKey) => {
      items.push({
        id: selection.variantId,
        quantity: 1,
        properties: {
          _build_id: buildId,
          _build_part: categoryKey,
        },
      });
    });

    this.submitButton.disabled = true;
    const originalText = this.submitButton.textContent;
    this.submitButton.textContent = '...';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (response.ok) {
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch {
          /* ignore */
        }
        window.location.href = '/checkout';
        return;
      }
    } catch {
      /* network error — fall through to re-enable button below */
    }

    this.submitButton.disabled = false;
    this.submitButton.textContent = originalText;
  };

  /**
   * @param {number} amount - decimal currency amount, e.g. 24.99
   */
  #formatMoney(amount) {
    const currency = this.dataset.currency || 'USD';
    const locale = document.documentElement.lang || 'ar';

    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
    } catch {
      return amount.toFixed(2);
    }
  }

  /**
   * @param {string} value
   */
  #escape(value) {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
  }
}

if (!customElements.get('wifi-pc-builder')) {
  customElements.define('wifi-pc-builder', WifiPcBuilder);
}
